import { GQL } from "@binale-tech/shared";

import type { MessageId } from "@domain/intl/useTranslation";
import { DmsType } from "@domain/models/dms";

import { withThinking } from "../runner/runAgent";
import type { AgentDetailItem, AgentStep, ReportLine } from "../runner/types";
import { isInYear } from "./inYear";
import { MAX_IDS_PER_JOB } from "./draftSelection";
import { sendInChunks } from "./paging";

const Outcome = GQL.ICashRegisterFromDocumentOutcome;
const Reason = GQL.ICashRegisterFromDocumentReason;

const refusedTexts: Record<GQL.ICashRegisterFromDocumentReason, MessageId> = {
    [Reason.NotFound]: "app.agent.details.kasse.refused.notFound",
    [Reason.NotKasseFolder]: "app.agent.details.kasse.refused.notKasseFolder",
    [Reason.NotProcessed]: "app.agent.details.kasse.refused.notProcessed",
    [Reason.NoAmount]: "app.agent.details.kasse.refused.noAmount",
    [Reason.SaveFailed]: "app.agent.details.kasse.refused.saveFailed",
};

const toDetail = (result: GQL.ICashRegisterFromDocumentResult, register: string, undated: boolean): AgentDetailItem => {
    const ref = { kind: "document" as const, id: result.documentId };
    const target = result.transactionId
        ? { target: { kind: "kasseTransaction" as const, id: result.transactionId } }
        : {};
    switch (result.outcome) {
        case Outcome.Created:
            if (undated) {
                return {
                    group: "needsYou",
                    ref,
                    ...target,
                    outcome: { textId: "app.agent.details.kasse.undatedBooked" },
                };
            }
            return {
                group: "done",
                ref,
                ...target,
                outcome: { textId: "app.agent.details.kasse.created", values: { register } },
            };
        case Outcome.AlreadyLinked:
            return { group: "untouched", ref, ...target, outcome: { textId: "app.agent.details.kasse.alreadyLinked" } };
        default:
            return {
                group: "refused",
                ref,
                outcome: {
                    textId:
                        (result.reason ? refusedTexts[result.reason] : undefined) ??
                        "app.agent.details.kasse.refused.unknown",
                },
            };
    }
};

export const kasseFromDocumentsStep: AgentStep = {
    id: "kasseFromDocuments",
    product: [GQL.IProductKey.CashRegister],
    async run(ctx, io) {
        // documents that already have a Kasse transaction are not sent again
        const linked = new Set(ctx.cashRegisterTransactions().flatMap((tx) => tx.documents.map((d) => String(d.id))));
        const candidates = [...ctx.documents().values()].filter(
            (doc) => doc.type === DmsType.KB && !linked.has(String(doc.key)),
        );
        // dated and undated sets are disjoint; the server books undated ones with today's date
        const workFor = (documents: typeof candidates) =>
            ctx
                .cashRegisterFolders()
                .map((folder) => ({
                    ...folder,
                    ids: documents.filter((doc) => doc.subType === folder.subTypeId).map((doc) => String(doc.key)),
                }))
                .filter((folder) => folder.ids.length > 0);
        const total = (folders: { ids: string[] }[]) => folders.reduce((sum, folder) => sum + folder.ids.length, 0);
        const dated = workFor(candidates.filter((doc) => isInYear(doc.documentDate, ctx.year)));
        // undated documents are booked with today's date, so only those created in the run's year and only in that year
        const undatedAll = workFor(candidates.filter((doc) => !doc.documentDate && isInYear(doc.createdAt, ctx.year)));
        const bookUndated = ctx.year === ctx.currentYear();
        const undated = bookUndated ? undatedAll : [];
        const manual: AgentDetailItem[] = bookUndated
            ? []
            : undatedAll.flatMap((folder) =>
                  folder.ids.map((id): AgentDetailItem => ({
                      group: "needsYou",
                      ref: { kind: "document", id },
                      outcome: { textId: "app.agent.details.kasse.undatedManual" },
                  })),
              );
        if (dated.length === 0 && undated.length === 0) {
            return manual.length > 0
                ? {
                      status: "nothing",
                      open: [{ textId: "app.agent.report.kasseUndatedManual", values: { count: manual.length } }],
                      details: manual,
                  }
                : { status: "nothing" };
        }

        const datedApproved =
            dated.length > 0 &&
            (await io.approve({
                textId: "app.agent.kasse.approve.question",
                values: { count: total(dated) },
                goLabelId: "app.agent.kasse.approve.go",
                learnMoreId: "app.agent.kasse.learnMore",
            }));
        const undatedApproved =
            undated.length > 0 &&
            (await io.approve({
                textId: "app.agent.kasse.approveUndated.question",
                values: { count: total(undated) },
                goLabelId: "app.agent.kasse.approveUndated.go",
                learnMoreId: "app.agent.kasse.learnMore",
                askInQuick: true,
            }));
        const manualOpen: ReportLine[] =
            manual.length > 0
                ? [{ textId: "app.agent.report.kasseUndatedManual", values: { count: manual.length } }]
                : [];
        if (!datedApproved && !undatedApproved) {
            return manual.length > 0
                ? { status: "skipped", byUser: true, open: manualOpen, details: manual }
                : { status: "skipped", byUser: true };
        }
        const work = [
            ...(datedApproved ? dated.map((folder) => ({ ...folder, undated: false })) : []),
            ...(undatedApproved ? undated.map((folder) => ({ ...folder, undated: true })) : []),
        ];

        let created = 0;
        let refused = 0;
        let undatedBooked = 0;
        const details: AgentDetailItem[] = [...manual];
        for (const folder of work) {
            if (io.isCancelled()) break;
            const results = await withThinking(io, "app.agent.kasse.thinking", () =>
                sendInChunks(folder.ids, MAX_IDS_PER_JOB, (chunk) =>
                    ctx.createKasseFromDocuments(folder.cashRegisterId, chunk),
                ),
            );
            const folderDetails = results.map((r) => toDetail(r, folder.name, folder.undated));
            details.push(...folderDetails);
            const count = (outcome: GQL.ICashRegisterFromDocumentOutcome) =>
                results.filter((r) => r.outcome === outcome).length;
            const made = count(Outcome.Created);
            created += made;
            if (folder.undated) undatedBooked += made;
            refused += count(Outcome.Refused);
            if (made > 0) {
                io.post({
                    kind: "done",
                    textId: "app.agent.kasse.done",
                    values: { count: made, register: folder.name },
                    details: folderDetails,
                });
            }
        }
        const open: ReportLine[] = [
            ...(refused > 0 ? [{ textId: "app.agent.report.kasseRefused" as const, values: { count: refused } }] : []),
            ...(undatedBooked > 0
                ? [{ textId: "app.agent.report.kasseUndatedBooked" as const, values: { count: undatedBooked } }]
                : []),
            ...manualOpen,
        ];
        const summary: ReportLine[] =
            created > 0 ? [{ textId: "app.agent.report.kasseCreated", values: { count: created } }] : [];
        return { status: created > 0 ? "done" : "nothing", summary, open, details };
    },
};
